import { CloseOutlined } from '@mui/icons-material';
import { Button, Dropdown } from 'finara-components';
import type { DropdownOption } from 'finara-components';
import React, { useState } from 'react';

import { Input } from '@/components/common/Input';
import { useDepartments } from '@/hooks/queries/useDepartments';
import { useRoles } from '@/hooks/queries/useRoles';
import { useAuth } from '@/hooks/useAuth';
import type { CreateEmployeeData } from '@/types/employees';

interface AddEmployeeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: CreateEmployeeData) => void;
  isLoading?: boolean;
}

export const AddEmployeeModal: React.FC<AddEmployeeModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  isLoading = false,
}) => {
  const [formData, setFormData] = useState<CreateEmployeeData>({
    name: '',
    email: '',
    password: '',
    roleId: '',
    departmentId: '',
    jobTitle: {
      name: '',
      description: '',
    },
    salary: {
      amount: 0,
      currency: 'MXN',
      frequency: 'monthly',
      type: 'base',
    },
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const { user } = useAuth();
  const organizationId = user?.organization?.id;

  // Fetch roles and departments
  const { data: rolesData } = useRoles(organizationId);
  const { data: departmentsData } = useDepartments();

  // Build dropdown options
  const roleOptions: DropdownOption[] = React.useMemo(() => {
    if (!rolesData?.data) return [];
    // Filter out super_admin role
    return rolesData.data
      .filter(role => role.name.toLowerCase() !== 'super_admin')
      .map(role => ({
        value: role.id,
        label: role.name,
      }));
  }, [rolesData]);

  const departmentOptions: DropdownOption[] = React.useMemo(() => {
    const options: DropdownOption[] = [{ value: '', label: 'Sin departamento' }];
    if (departmentsData?.data) {
      departmentsData.data.forEach(dept => {
        options.push({ value: dept.id, label: dept.name });
      });
    }
    return options;
  }, [departmentsData]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors['name'] = 'El nombre es requerido';
    }

    if (!formData.email.trim()) {
      newErrors['email'] = 'El email es requerido';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors['email'] = 'Email inválido';
    }

    if (!formData.password.trim()) {
      newErrors['password'] = 'La contraseña es requerida';
    } else if (formData.password.length < 8) {
      newErrors['password'] = 'La contraseña debe tener al menos 8 caracteres';
    }

    if (!formData.roleId) {
      newErrors['roleId'] = 'El rol es requerido';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    // Clean up optional fields if empty
    const submitData: CreateEmployeeData = {
      name: formData.name,
      email: formData.email,
      password: formData.password,
      roleId: formData.roleId,
    };

    if (formData.departmentId) {
      submitData.departmentId = formData.departmentId;
    }

    if (formData.jobTitle?.name) {
      submitData.jobTitle = {
        name: formData.jobTitle.name,
        description: formData.jobTitle.description || '',
      };
    }

    if (formData.salary && formData.salary.amount > 0) {
      submitData.salary = formData.salary;
    }

    onSubmit(submitData);
  };

  const handleClose = () => {
    setFormData({
      name: '',
      email: '',
      password: '',
      roleId: '',
      departmentId: '',
      jobTitle: { name: '', description: '' },
      salary: { amount: 0, currency: 'MXN', frequency: 'monthly', type: 'base' },
    });
    setErrors({});
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-black opacity-60 transition-opacity"
          onClick={handleClose}
          role="button"
          tabIndex={0}
          onKeyDown={e => {
            if (e.key === 'Escape') handleClose();
          }}
          aria-label="Cerrar modal"
        />

        {/* Modal */}
        <div className="relative bg-surface rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-border">
            <h2 className="text-xl font-semibold text-brand-white">Agregar Empleado</h2>
            <button
              onClick={handleClose}
              className="text-muted hover:text-brand-white transition-colors"
              disabled={isLoading}
            >
              <CloseOutlined />
            </button>
          </div>

          {/* Body */}
          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            {/* Basic Information */}
            <div>
              <h3 className="text-sm font-semibold text-brand-white mb-4">Información Básica</h3>
              <div className="space-y-4">
                <div>
                  <Input
                    id="name"
                    label="Nombre Completo"
                    type="text"
                    value={formData.name}
                    onChange={e => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="Ej: Juan Pérez García"
                    required
                  />
                  {errors['name'] && <p className="text-sm text-red-600 mt-1">{errors['name']}</p>}
                </div>

                <div>
                  <Input
                    id="email"
                    label="Email"
                    type="email"
                    value={formData.email}
                    onChange={e => setFormData(prev => ({ ...prev, email: e.target.value }))}
                    placeholder="Ej: juan.perez@empresa.com"
                    required
                  />
                  {errors['email'] && (
                    <p className="text-sm text-red-600 mt-1">{errors['email']}</p>
                  )}
                </div>

                <div>
                  <Input
                    id="password"
                    label="Contraseña"
                    type="password"
                    value={formData.password}
                    onChange={e => setFormData(prev => ({ ...prev, password: e.target.value }))}
                    placeholder="Mínimo 8 caracteres"
                    required
                  />
                  {errors['password'] && (
                    <p className="text-sm text-red-600 mt-1">{errors['password']}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Role and Department */}
            <div>
              <h3 className="text-sm font-semibold text-brand-white mb-4">Rol y Departamento</h3>
              <div className="space-y-4">
                <div>
                  <label htmlFor="roleId" className="block text-sm font-medium text-brand-white mb-1">
                    Rol <span className="text-red-500">*</span>
                  </label>
                  <Dropdown
                    id="roleId"
                    value={formData.roleId}
                    onChange={value => setFormData(prev => ({ ...prev, roleId: String(value) }))}
                    options={roleOptions}
                    placeholder="Selecciona un rol"
                  />
                  {errors['roleId'] && (
                    <p className="text-sm text-red-600 mt-1">{errors['roleId']}</p>
                  )}
                </div>

                <div>
                  <label
                    htmlFor="departmentId"
                    className="block text-sm font-medium text-brand-white mb-1"
                  >
                    Departamento
                  </label>
                  <Dropdown
                    id="departmentId"
                    value={formData.departmentId || ''}
                    onChange={value =>
                      setFormData(prev => ({ ...prev, departmentId: String(value) }))
                    }
                    options={departmentOptions}
                    placeholder="Selecciona un departamento"
                    searchable
                  />
                </div>
              </div>
            </div>

            {/* Job Title (Optional) */}
            <div>
              <h3 className="text-sm font-semibold text-brand-white mb-4">
                Puesto <span className="text-sm font-normal text-muted">(Opcional)</span>
              </h3>
              <div className="space-y-4">
                <Input
                  id="jobTitleName"
                  label="Nombre del Puesto"
                  type="text"
                  value={formData.jobTitle?.name || ''}
                  onChange={e =>
                    setFormData(prev => ({
                      ...prev,
                      jobTitle: {
                        name: e.target.value || '',
                        description: prev.jobTitle?.description || '',
                      },
                    }))
                  }
                  placeholder="Ej: Desarrollador Senior"
                />

                <Input
                  id="jobTitleDescription"
                  label="Descripción del Puesto"
                  type="text"
                  value={formData.jobTitle?.description || ''}
                  onChange={e =>
                    setFormData(prev => ({
                      ...prev,
                      jobTitle: {
                        name: prev.jobTitle?.name || '',
                        description: e.target.value || '',
                      },
                    }))
                  }
                  placeholder="Ej: Responsable del desarrollo de software"
                />
              </div>
            </div>

            {/* Salary (Optional) */}
            <div>
              <h3 className="text-sm font-semibold text-brand-white mb-4">
                Salario <span className="text-sm font-normal text-muted">(Opcional)</span>
              </h3>
              <div className="space-y-4">
                <Input
                  id="salaryAmount"
                  label="Monto Mensual"
                  type="number"
                  value={formData.salary?.amount || 0}
                  onChange={e =>
                    setFormData(prev => ({
                      ...prev,
                      salary: { ...prev.salary!, amount: Number(e.target.value) },
                    }))
                  }
                  placeholder="Ej: 45000"
                />
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-3 pt-6 border-t border-border">
              <Button variant="white" onClick={handleClose} disabled={isLoading}>
                Cancelar
              </Button>
              <Button type="submit" variant="black" disabled={isLoading}>
                {isLoading ? 'Creando...' : 'Crear Empleado'}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};
